import React from 'react';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import { Spring } from 'react-spring/renderprops';
import styled from 'styled-components';

import { ISwipeStep, ComponentStageType } from '../../types';

import { getFadeInOutProps } from '../../transitions';

// Components
import { CardOuter } from './CardOuter';
import { CardInner } from './CardInner';
import { MobileOnly } from './MobileOnly';
import { SwipeContainer } from './SwipeContainer';
import { QuestionHeader } from './QuestionHeader';
import { BackgroundCard } from './BackgroundCard';
import { FullHeightContainer } from '../Containers';
import { Controls } from './Controls';

// Other
import { colors } from '../../constants';
import { createMarkup } from '../../utils';

interface IProps {
    question: ISwipeStep;
    nextQuestion: ISwipeStep | null;
    componentStage: ComponentStageType;
    animateCardTransition: boolean;
    className?: string;
    onAnimationExit(): void;
    onNoSwipe(): void;
    onYesSwipe(): void;
}

interface IState {
    choice: boolean | null;
    hideCard: boolean;
    cardStartingPosition: ClientRect | null;
    cardAnimationStage: ComponentStageType;
}

class BaseSwipeDisplay extends React.Component<IProps, IState> {
    cardX: number;
    cardInnerRef: React.RefObject<HTMLInputElement>;

    constructor(props: IProps) {
        super(props);
        this.cardInnerRef = React.createRef();
        this.cardX = 0;
        this.state = {
            choice: null,
            hideCard: false,
            cardStartingPosition: null,
            cardAnimationStage: 'enter',
        };
    }

    componentDidMount() {
        this.setCardStartingPosition();

        window.addEventListener('resize', this.setCardStartingPosition);
    }

    componentDidUpdate(prevProps: IProps) {
        if (this.props.question !== prevProps.question) {
            this.cardX = 0;
            this.rotateCard();

            this.setState({
                choice: null,
                hideCard: false,
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.setCardStartingPosition);
    }

    setCardStartingPosition = () => {
        if (!this.cardInnerRef || !this.cardInnerRef.current) return;

        const cardStartingPosition = this.cardInnerRef.current.getBoundingClientRect();

        this.setState({
            cardStartingPosition,
        });
    };

    handleDrag = (e: DraggableEvent, data: DraggableData) => {
        const choice = data.x > 0 ? true : data.x < 0 ? false : null;

        this.cardX = data.x;
        this.rotateCard();

        if (choice !== this.state.choice) {
            this.setState({
                choice,
            });
        }
    };

    handleDragEnd = (e: DraggableEvent, data: DraggableData) => {
        const { onYesSwipe, onNoSwipe } = this.props;
        const isRelevantMovement = data.x >= 100 || data.x <= -100;

        if (isRelevantMovement) {
            this.setState({
                hideCard: true,
            });

            return Math.sign(data.x) === 1 ? onYesSwipe() : onNoSwipe();
        }

        this.cardX = 0;
        this.rotateCard();

        return this.setState({
            choice: null,
        });
    };

    handleYesClick = () => {
        this.setState({
            choice: true,
            cardAnimationStage: 'exit',
        });
    };

    handleNoClick = () => {
        this.setState({
            choice: false,
            cardAnimationStage: 'exit',
        });
    };

    handleCardAnimationExit = () => {
        const { cardAnimationStage, choice } = this.state;
        const { onYesSwipe, onNoSwipe, nextQuestion } = this.props;

        if (cardAnimationStage !== 'exit') return;

        choice ? onYesSwipe() : onNoSwipe();

        if (nextQuestion) {
            this.setState({
                choice: null,
                cardAnimationStage: 'enter',
            });
        }
    };

    getCardColor() {
        const { choice } = this.state;

        if (choice === null) {
            return colors.greenBg;
        }

        return choice ? colors.swipeYes : colors.swipeNo;
    }

    getButtonColor = (buttonValue: boolean) => {
        const { choice } = this.state;

        if (choice !== null && buttonValue === choice) {
            return choice ? colors.swipeYes : colors.swipeNo;
        }

        return colors.greenPrimary;
    };

    rotateCard() {
        if (!this.cardInnerRef || !this.cardInnerRef.current) return;

        const ROTATION_FACTOR = 0.125;
        const cardRotation = `rotate(${this.cardX * ROTATION_FACTOR}deg)`;

        this.cardInnerRef.current.style.transform =
            this.cardX === 0 ? 'none' : cardRotation;
    }

    render() {
        const {
            question,
            nextQuestion,
            componentStage,
            onAnimationExit,
            animateCardTransition,
            className,
        } = this.props;
        const {
            cardStartingPosition,
            hideCard,
            cardAnimationStage,
        } = this.state;
        const dragged = this.cardX !== 0;
        const cardSpringProps = getFadeInOutProps({
            stage: cardAnimationStage,
            onRest: this.handleCardAnimationExit,
            immediate: !animateCardTransition,
        });

        return (
            <div className={className}>
                <FullHeightContainer>
                    <QuestionHeader
                        stage={componentStage}
                        questionText={question.text}
                    />
                    <SwipeContainer>
                        {nextQuestion && cardStartingPosition && (
                            <BackgroundCard
                                position={cardStartingPosition}
                                nextQuestionText={nextQuestion.text}
                                nextQuestionSubText={nextQuestion.subText}
                            />
                        )}
                        <Draggable
                            onStop={this.handleDragEnd}
                            onDrag={this.handleDrag}
                            position={{ x: this.cardX, y: 0 }}
                            axis="x"
                        >
                            <CardOuter hideCard={hideCard}>
                                <Spring {...cardSpringProps}>
                                    {(animationProps) => (
                                        <CardInner
                                            cardColor={this.getCardColor()}
                                            dragged={dragged}
                                            ref={this.cardInnerRef}
                                            style={animationProps}
                                        >
                                            <MobileOnly>
                                                <div
                                                    dangerouslySetInnerHTML={createMarkup(
                                                        question.text
                                                    )}
                                                />
                                                {question.subText && (
                                                    <div>
                                                        {question.subText}
                                                    </div>
                                                )}
                                            </MobileOnly>
                                        </CardInner>
                                    )}
                                </Spring>
                            </CardOuter>
                        </Draggable>
                    </SwipeContainer>
                </FullHeightContainer>
                <Controls
                    stage={componentStage}
                    onAnimationExit={onAnimationExit}
                    getButtonColor={this.getButtonColor}
                    onNoClick={this.handleNoClick}
                    onYesClick={this.handleYesClick}
                />
            </div>
        );
    }
}

export const SwipeDisplay = styled(BaseSwipeDisplay)<IProps>`
    display: flex;
    flex-direction: column;
    height: 100%;
`;
